import { useState, useMemo } from "react";
import { format } from "date-fns";
import { ja } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * 配送日付け選択ピッカー
 * - 配送スケジュールがある日付をハイライト表示
 * - スケジュールが無い日付は選択不可
 *
 * Props:
 *   value: "YYYY-MM-DD" 文字列 or ""
 *   onChange: (dateString: string) => void
 *   schedules: [{delivery_date: "YYYY-MM-DD", ...}]
 *   testId?: data-testid for trigger button
 *   placeholder?: string
 *   disabled?: boolean
 *   allowAnyDate?: boolean - true なら全日付選択可（ハイライトのみ）
 */
export default function DeliveryDatePicker({
  value,
  onChange,
  schedules = [],
  testId,
  placeholder = "配送日を選択",
  disabled = false,
  allowAnyDate = false,
}) {
  const [open, setOpen] = useState(false);

  const scheduleDates = useMemo(() => {
    return schedules.map((s) => parseDateString(s.delivery_date));
  }, [schedules]);

  const scheduleDateSet = useMemo(() => {
    return new Set(schedules.map((s) => s.delivery_date));
  }, [schedules]);

  const selectedDate = value ? parseDateString(value) : undefined;

  const handleSelect = (date) => {
    if (!date) return;
    const ds = format(date, "yyyy-MM-dd");
    onChange(ds);
    setOpen(false);
  };

  const isAvailable = (date) => {
    const ds = format(date, "yyyy-MM-dd");
    return scheduleDateSet.has(ds);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid={testId}
          disabled={disabled}
          className={cn(
            "h-9 justify-start font-normal rounded-sm border-slate-300",
            !value && "text-slate-500"
          )}
        >
          <CalendarIcon className="h-4 w-4 mr-2" />
          {value ? (
            <span>
              {value}{" "}
              <span className="text-blue-600 font-medium">
                ({format(parseDateString(value), "EEE", { locale: ja })})
              </span>
            </span>
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-50" align="start">
        <Calendar
          mode="single"
          locale={ja}
          selected={selectedDate}
          onSelect={handleSelect}
          modifiers={{ available: scheduleDates }}
          modifiersClassNames={{
            available:
              "bg-emerald-50 text-emerald-700 font-semibold hover:bg-emerald-100",
          }}
          disabled={allowAnyDate ? undefined : (date) => !isAvailable(date)}
          initialFocus
        />
        <div className="border-t border-slate-200 px-3 py-2 text-xs text-slate-600 bg-slate-50 flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-300" />
          配送日あり
        </div>
      </PopoverContent>
    </Popover>
  );
}

// "YYYY-MM-DD" を Date(ローカル) に変換（タイムゾーンずれ防止）
function parseDateString(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
